// 設定 → 開發者帳號（plan SPEC #account-split-app-link、#account-split-ui「VMS 使用者選單 › 開發者帳號」）：
// 輸入開發者入口產生的 8 碼 → 預覽是哪個開發者帳號 → 確認（5 分鐘內重新驗證）；已連結的可以解除。
// 碼只放元件狀態，不進網址、不存瀏覽器。
// 連結種類（preview.kind）：self = 本人連結（「兩個帳號都是你本人的才確認」）；grant = 跨人 / lab 額度授權連結，
// 確認頁換成跨人版文案（只開 AID 額度配設備、不一定是本人的帳號），已連結列標「額度授權」；
// 碼不是給這個帳號的 → 422 LINK_TARGET_MISMATCH（SPEC #account-split-errors）。
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirm } from '../../components/ConfirmDialog';
import { SessionError, confirmAppLink, deleteAppLink, listAppLinks, previewAppLink, type AppLinkPreview } from '../../lib/session';
import { useApp } from '../../lib/AppContext';
import { stamp } from '../../lib/timefmt';
import { EmailVerifyPrompt, emailVerifyRequired } from '../../components/EmailVerifyPrompt';

export function DeveloperAccountSection() {
  const { t } = useTranslation();
  const { ask, dialog } = useConfirm();
  const { config: cfg } = useApp();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['app-links'], queryFn: () => listAppLinks() });
  const [code, setCode] = useState('');
  const [preview, setPreview] = useState<AppLinkPreview | null>(null);
  const [err, setErr] = useState<string | null>(null);
  /** 403 EMAIL_NOT_VERIFIED →「先驗證你的 email」（kq Q143）；處理了回 true。 */
  const [emailVerify, setEmailVerify] = useState<{ resend: boolean } | null>(null);
  const askEmailVerify = (e: unknown) => {
    const ev = emailVerifyRequired(e);
    if (ev) setEmailVerify(ev);
    return ev !== null;
  };
  const [done, setDone] = useState<string | null>(null);
  const isDev = cfg.env === 'dev';
  const showErr = (e: unknown) => {
    if (askEmailVerify(e)) return;
    const c = e instanceof SessionError ? e.code : 'default';
    let msg = t(`devacct.err_${c}`, { defaultValue: t('devacct.err_default', { code: c }) });
    if (c === 'LINK_CODE_GONE' && !isDev) msg += ' ' + t('devacct.err_gone_dev_hint');
    setErr(msg);
  };
  const refresh = () => void qc.invalidateQueries({ queryKey: ['app-links'] });
  const look = useMutation({
    mutationFn: () => previewAppLink(clean),
    onSuccess: (p) => (setPreview(p), setErr(null), setDone(null)),
    onError: (e) => (setPreview(null), showErr(e)),
  });
  const confirm = useMutation({
    mutationFn: () => confirmAppLink(clean),
    onSuccess: () => {
      const email = preview?.portal_email ?? '';
      setDone(preview?.kind === 'grant' ? t('devacct.grant_done', { email }) : t('devacct.linked_done', { email }));
      setPreview(null);
      setCode('');
      setErr(null);
      refresh();
    },
    onError: showErr,
  });
  const del = useMutation({ mutationFn: (id: string) => deleteAppLink(id), onSuccess: () => (setErr(null), setDone(null), refresh()), onError: showErr });
  const links = q.data ?? [];
  const clean = code.replace(/[\s-]/g, '');
  const grant = preview?.kind === 'grant';
  return (
    <section className="card" data-testid="developer-account">
      <h2 className="section-title">{t('devacct.title')}</h2>
      <p className="muted small">{t('devacct.hint')}</p>
      {err && (
        <p className="error" role="alert">
          {err}
        </p>
      )}
      {done && (
        <p className="notice" role="status">
          {done}
        </p>
      )}
      {q.isLoading && <p className="muted">{t('common.loading')}</p>}
      {links.length > 0 && (
        <ul className="plain-list">
          {links.map((l) => (
            <li key={l.link_id} className="row" data-testid="devacct-link" data-kind={l.kind ?? 'self'}>
              <strong>{l.portal_email}</strong>
              {l.kind === 'grant' && (
                <span className="badge" data-testid="devacct-kind">
                  {t('devacct.kind_grant')}
                </span>
              )}
              <span className="muted small">{t(l.kind === 'grant' ? 'devacct.grant_linked_at' : 'devacct.linked_at', { date: stamp(l.created_at) })}</span>
              <div className="spacer" />
              <button
                className="btn btn-sm"
                disabled={del.isPending}
                onClick={() => {
                  void ask({ danger: true, title: t(l.kind === 'grant' ? 'devacct.grant_unlink_confirm' : 'devacct.unlink_confirm'), action: t('devacct.unlink') }).then(
                    (ok) => ok && del.mutate(l.link_id),
                  );
                }}
              >
                {t('devacct.unlink')}
              </button>
            </li>
          ))}
        </ul>
      )}
      {links.length === 0 && !q.isLoading && !preview && (
        <form
          className="toolbar"
          onSubmit={(e) => {
            e.preventDefault();
            if (clean.length === 8) look.mutate();
          }}
        >
          <label className="field">
            <span>{t('devacct.code_label')}</span>
            <input
              className="input mono"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              maxLength={11}
              autoComplete="off"
              spellCheck={false}
              placeholder="XXXX XXXX"
              data-testid="devacct-code"
            />
          </label>
          <button className="btn btn-primary" disabled={clean.length !== 8 || look.isPending} data-testid="devacct-next">
            {t('devacct.next')}
          </button>
        </form>
      )}
      {links.length === 0 && !preview && <p className="muted small">{isDev ? t('devacct.where_dev') : t('devacct.where')}</p>}
      {preview && (
        <div className="notice" data-testid="devacct-preview" data-kind={preview.kind ?? 'self'}>
          <h3>{grant ? t('devacct.grant_title') : t('devacct.confirm_title')}</h3>
          <p>{grant ? t('devacct.grant_who', { email: preview.portal_email, brand: cfg.display_name }) : t('devacct.confirm_who', { email: preview.portal_email })}</p>
          <p>{grant ? t('devacct.grant_can') : isDev ? t('devacct.confirm_can_dev') : t('devacct.confirm_can_prod')}</p>
          <div className="toolbar">
            <button className="btn btn-primary" disabled={confirm.isPending} onClick={() => confirm.mutate()} data-testid="devacct-confirm">
              {grant ? t('devacct.grant_confirm') : t('devacct.confirm')}
            </button>
            <button className="btn" onClick={() => (setPreview(null), setErr(null))}>
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}
      {dialog}
      {emailVerify && <EmailVerifyPrompt resend={emailVerify.resend} onClose={() => setEmailVerify(null)} onVerified={() => confirm.mutate()} />}
    </section>
  );
}
